/* THE WARD, AS A PLAN. A Nightingale ward: the beds along two walls facing one another, the nurses' station at the
   entrance. A bed's place is a function of its number and nothing else, in whole units, so the same bed is at the same
   coordinates in every release, on every reload, whatever the world or the run: nothing here reads data to place a bed.
   What data decides is how many beds there are (the world's corridor table has one entry per bay) and the one mark each
   bed wears (the outcome of the cases the last analysed run played in that bay). There is no colour for the normal state. */
import type { CaseRow, Run } from '../types.ts'

/** One module of the plan, in plan units. Beds, aisle and station are whole multiples of it. */
export const MODULE = 8
const WALL_W = 4 * MODULE
const BED_W = 4 * MODULE
const BED_H = 3 * MODULE
const PAIR_PITCH = 7 * MODULE
const TOP = 6 * MODULE
const WIDTH = 30 * MODULE
const WEST = WALL_W // west beds start at the west wall, headboard against it
const EAST = WIDTH - WALL_W - BED_W

export type Bed = { n: number; side: 'west' | 'east'; x: number; y: number; w: number; h: number; cx: number; cy: number }

/** Where bed `n` is. Odd beds on the west wall, even on the east, a pair every seven modules down the ward. Fixed forever. */
export function bedAt(n: number): Bed {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`There is no bed ${n}`)
  const pair = Math.floor((n - 1) / 2)
  const west = n % 2 === 1
  const x = west ? WEST : EAST
  const y = TOP + pair * PAIR_PITCH
  return { n, side: west ? 'west' : 'east', x, y, w: BED_W, h: BED_H, cx: x + BED_W / 2, cy: y + BED_H / 2 }
}

export type Plan = { width: number; height: number; beds: Bed[]; station: { x: number; y: number; w: number; h: number }; aisle: { x: number } }

/** The plan for `count` beds: the same bed coordinates for any count; only the ward's length changes. */
export function planOf(count: number): Plan {
  const beds = Array.from({ length: Math.max(0, count) }, (_, i) => bedAt(i + 1))
  const pairs = Math.ceil(beds.length / 2)
  const stationY = TOP + pairs * PAIR_PITCH
  return { width: WIDTH, height: stationY + 5 * MODULE, beds, station: { x: WIDTH / 2 - 4 * MODULE, y: stationY, w: 8 * MODULE, h: 3 * MODULE }, aisle: { x: WIDTH / 2 } }
}

/** The bay table the world publishes (unit.bay_distance), one corridor-minute entry per bay: how many beds, and each bed's minutes from the supply point. */
export function baysOf(floors: readonly { path: string; value: unknown }[]): { count: number; minutes: number[] } | null {
  const f = floors.find((x) => x.path === 'unit.bay_distance')
  if (!f || !Array.isArray(f.value) || f.value.length === 0 || !f.value.every((v) => typeof v === 'number')) return null
  return { count: f.value.length, minutes: f.value as number[] }
}

export type BedMark = 'empty' | 'done' | 'short' | 'harm' | 'lost'
export const BED_MARK_TEXT: Record<BedMark, string> = {
  empty: 'No case played in this bay',
  done: 'Every case here ended with the patient well and no harm',
  short: 'A case here ended without the patient reaching the aim',
  harm: 'A case here recorded harm',
  lost: 'A patient died here',
}
const WORST: BedMark[] = ['empty', 'done', 'short', 'harm', 'lost']

/** One case's mark: death outranks harm, harm outranks an aim missed, an aim missed outranks done. */
export function caseMark(c: Pick<CaseRow, 'solved' | 'died' | 'harms'>): BedMark {
  return c.died ? 'lost' : c.harms > 0 ? 'harm' : c.solved ? 'done' : 'short'
}

/** The mark of each bed from the cases of one analysed run: the gravest mark among the cases played in that bay. A bay with no case is empty. */
export function bedMarks(count: number, cases: readonly Pick<CaseRow, 'bay' | 'solved' | 'died' | 'harms'>[]): BedMark[] {
  const marks: BedMark[] = Array.from({ length: count }, () => 'empty')
  for (const c of cases) {
    const i = c.bay - 1
    if (i < 0 || i >= count) continue
    const m = caseMark(c)
    if (WORST.indexOf(m) > WORST.indexOf(marks[i])) marks[i] = m
  }
  return marks
}

/** The marks of a column row. The meaning of a mark is given by the column's own key, and none of them is a number. */
export type RowMark = 'empty' | 'done' | 'short' | 'live' | 'withdrawn'

/** The run whose cases mark the beds: the newest run the server has scored (complete or stopped with cases played), by the time it was opened. */
export function analysedRun(runs: readonly Run[]): Run | null {
  const done = runs.filter((r) => (r.status === 'complete' || r.status === 'stopped') && !r.nothing_played)
  return done.sort((a, b) => b.created.localeCompare(a.created) || b.id.localeCompare(a.id))[0] ?? null
}
